import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getActiveBanks,
  ListOfPdfs,
  saveBankProcess,
} from "../../../../../api";
import CrossIcon from "../../../../assets/svg/icons/common/cross";
import TagInput from "../../../../components/form/tag-input";
import Spinner from "../../../../components/Loaders/Spinner";
import useCommonStore from "../../../../stores/common";
import useOrganizationStore from "../../../../stores/organization";
import FileCard from "./FileCard";
import styles from "./styles.module.scss";

const Chip = ({ name, onDelete }) => {
  return (
    <div className={styles.chip}>
      <span className={styles.chipText}>{name}</span>
      <span onClick={onDelete} className={styles.deleteIcon}>
        <CrossIcon />
      </span>
    </div>
  );
};

const Overrides = () => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData,
  );
  const [addendumOptions, setAddendumOptions] = useState([]);
  const [selectedAddendums, setSelectedAddendums] = useState(
    selectedOrganizationData?.organization?.bank_addendums || [],
  );
  const [bankOptions, setBankOptions] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState(
    selectedOrganizationData?.organization?.bank_applications || [],
  );
  const [loadingAddendums, setLoadingAddendums] = useState(true);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAddendums = async () => {
    try {
      setLoadingAddendums(true);
      const response = await ListOfPdfs();
      if (!response?.data?.addendums_names) {
        console.error("There was an error fetching addendums");
        return;
      }

      setAddendumOptions(
        response.data.addendums_names.map((file_name) => ({
          value: file_name,
          label: file_name,
        })),
      );
      setLoadingAddendums(false);
    } catch (error) {
      console.log(error);
    }
  };

  const loadActiveBanks = async () => {
    try {
      setLoadingBanks(true);
      const response = await getActiveBanks();
      if (!response.status) {
        console.error("There was an error fetching banks");
        return;
      }

      setBankOptions(
        response.data.map((item) => ({
          value: item.id,
          label: item.name,
        })),
      );
      setLoadingBanks(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await loadAddendums();
      await loadActiveBanks();
    };

    fetchData();
  }, []);

  const saveBankProcessors = useCallback(
    async (selectedBanks, selectedAddendums) => {
      setSaving(true);
      toast.dismiss();
      try {
        const response = await saveBankProcess({
          bank_application: selectedBanks,
          bank_addendum: selectedAddendums,
          organization_id: selectedOrganizationId,
        });

        if (!response?.status) {
          console.error(response);
          toast.error("There was a problem saving bank processors");
          return;
        }

        setSelectedOrganizationData((prev) => ({
          ...prev,
          organization: {
            ...prev.organization,
            bank_applications: selectedBanks,
            bank_addendums: selectedAddendums,
          },
        }));

        toast.success("Bank processors saved successfully");
      } catch (error) {
        console.error(error);
        toast.error("There was a problem saving bank processors");
      } finally {
        setSaving(false);
      }
    },
    [selectedOrganizationId, setSelectedOrganizationData],
  );

  const handleOptionRemove = useCallback(
    async (option, type) => {
      const updatedItems =
        type === "bank"
          ? selectedBanks.filter((item) => item.value !== option.value)
          : selectedAddendums.filter((item) => item.value !== option.value);

      if (type === "bank") {
        setSelectedBanks(updatedItems);
      } else {
        setSelectedAddendums(updatedItems);
      }

      await saveBankProcessors(
        type === "bank" ? updatedItems : selectedBanks,
        type === "addendum" ? updatedItems : selectedAddendums,
      );
    },
    [saveBankProcessors, selectedAddendums, selectedBanks],
  );

  if (loadingAddendums || loadingBanks) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "32px" }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${expandDetails ? styles.expanded : ""}`}
    >
      <div className={styles.section}>
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>Bank Applications</h2>
          <div className={styles.searchInputContainer}>
            <TagInput
              placeholder="Search"
              onChange={(newSelected) => {
                setSelectedBanks(newSelected);
                saveBankProcessors(newSelected, selectedAddendums);
              }}
              options={bankOptions}
              value={selectedBanks}
              hideChips
            />
          </div>
        </div>
        <div className={styles.bankAppsContainer}>
          {selectedBanks?.map((app) => (
            <Chip
              key={app.value}
              name={app.label}
              onDelete={() => handleOptionRemove(app, "bank")}
            />
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>Bank Addendums</h2>
          <div className={styles.searchInputContainer}>
            <TagInput
              placeholder="Search"
              onChange={(newSelected) => {
                setSelectedAddendums(newSelected);
                saveBankProcessors(selectedBanks, newSelected);
              }}
              options={addendumOptions}
              value={selectedAddendums}
              hideChips
            />
          </div>
        </div>
        <div className={styles.fileGrid}>
          {selectedAddendums?.map((file) => (
            <FileCard
              key={file.value}
              name={file.value}
              onDelete={() => handleOptionRemove(file, "addendum")}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Overrides;
